import { SearchResult } from 'app/concierge/types';
import { ITileInfoData } from 'api/concierge';
import { getViewUrl } from 'tools/tools';
import { openExternalView } from 'app/element';
import { alpha, Box, Stack, Tooltip } from '@mui/material';
import DatabaseIcon from 'components/common/icon/DatabaseIcon';
import IconMi from 'components/common/icon/IconMi';
import { TileInfoIcon } from 'components/tile-info-icon';
import React, { useMemo } from 'react';
import { renderHtml, renderShowdown } from 'tools/safeHtml';
import useBundleTranslation from 'i18n';
import { AccessDeniedTileInfo } from 'app/home/index.functions';
import striptags from 'striptags';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from '../styles';
import { useSelector } from 'react-redux';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { dispatchCustomEvent } from 'tools/events';
import { useMiNavigation } from 'hooks/useNavigation';
import ReportWhyLine from '../../parts/ReportWhyLine';

export default function ElementResultItem({
    result,
    tileInfo,
    setAccessDeniedPopupTile,
    useInfoButton,
}: {
    result: SearchResult;
    tileInfo: ITileInfoData;
    setAccessDeniedPopupTile: (tile: AccessDeniedTileInfo | null) => void;
    useInfoButton?: boolean;
}) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;
    const isAppPanel = useSelector((state: any) => state.concierge.isAppPanel) as boolean;
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.activeChatMessageResultItem, 'item');
    }, [customStyles]);

    const itemId = `${result.id}_${result.second_id}`;

    const navigate = useMiNavigation();

    const description =
        tileInfo.description_markdown_ind === 'Y'
            ? renderShowdown(String(tileInfo.element_info))
            : String(tileInfo.element_info);

    const reportingTool = tileInfo.reporting_tool_name || tileInfo.external_content_name || '';
    // The icon map is keyed on the content type's alias; the tool name is only a fallback for a
    // tile that predates the alias, and stays the label.
    const iconName = tileInfo.content_type_alias || reportingTool;

    const noAccess =
        tileInfo &&
        'string' === typeof tileInfo.user_dashboard_element_instance_id &&
        0 === tileInfo.user_dashboard_element_instance_id.indexOf('no_access');

    const onItemClick = async () => {
        const elementId = tileInfo.element_id;
        const segmentLinkChunk = Number(tileInfo.segment_value_id) > 0 ? `segment/${tileInfo.segment_value_id}` : '';

        const viewUrl = getViewUrl(tileInfo.element_type as string);
        const elementLink = `${viewUrl}${elementId}/${segmentLinkChunk}`;

        if (isAppPanel) {
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'item-click',
                payload: {
                    item: result,
                    info: tileInfo,
                },
            });
        } else if (isMobile) {
            window.location.href = elementLink;
        } else {
            if (noAccess) {
                setAccessDeniedPopupTile({
                    element_id: Number(result.id),
                    segment_value_id: result.second_id,
                });
                return;
            }

            if ('external' == tileInfo.external_report_display) {
                await openExternalView(Number(tileInfo.element_id), tileInfo.segment_value_id ?? 0);
            } else {
                navigate(elementLink);
            }
        }
    };

    const getTileName = () => {
        const name = (
            <Box sx={styles.link} onClick={() => onItemClick()}>
                {result.name}
            </Box>
        );

        if ('external' == tileInfo.external_report_display) {
            return (
                <Tooltip
                    title={
                        <Stack direction={'row'} spacing={1} onClick={() => onItemClick()}>
                            <Box sx={styles.link}>{t('message.open_in', { source: reportingTool })}</Box>
                            <Box sx={styles.link}>
                                <IconMi sx={{ fontSize: 16 }} icon={'external-link'} />
                            </Box>
                        </Stack>
                    }
                    placement={'top'}
                >
                    {name}
                </Tooltip>
            );
        }

        return name;
    };

    const onInfoIconClick = () => {
        if (isAppPanel && useInfoButton) {
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'item-info-icon-click',
                payload: {
                    item: result,
                    info: tileInfo,
                },
            });
        }
        if (isMobile && useInfoButton) {
            const elementId = tileInfo.element_id;
            const segmentLinkChunk =
                Number(tileInfo.segment_value_id) > 0 ? `segment/${tileInfo.segment_value_id}` : '';

            window.location.href = `/info/${elementId}/${segmentLinkChunk}`;
        }
    };

    return (
        <Stack sx={styles.item} key={itemId}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <Stack direction={'row'} spacing={1}>
                    <Box sx={styles.iconHolder}>
                        <DatabaseIcon name={iconName} placeholderIconName={'metric'} />
                    </Box>
                    {getTileName()}
                </Stack>
                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    {noAccess && (
                        <Tooltip title={t('message.no_access_to_element')} placement={'top'}>
                            <Box sx={{ height: '16px', width: '16px' }}>
                                <IconMi icon={'lock'} fontSize={'15'} />
                            </Box>
                        </Tooltip>
                    )}
                    {useInfoButton && (
                        <Box
                            sx={{ cursor: 'pointer' }}
                            className={`tile-element__info ${tileInfo.certified_ind === 'Y' ? 'tile-element__info--certified' : ''}`}
                            onClick={onInfoIconClick}
                        >
                            {tileInfo.certified_ind === 'Y' ? (
                                <Box
                                    className={'tile-element__info--certified'}
                                    sx={{
                                        display: 'flex',
                                        color: tileInfo.certification_level_color || 'success.main',
                                        fontSize: '16px',
                                    }}
                                >
                                    <IconMi icon={'certified'} />
                                </Box>
                            ) : (
                                <Box
                                    className={'tile-element__info--default'}
                                    sx={{
                                        display: 'flex',
                                        color: (theme) => alpha(theme.palette.text.primary, 0.64),
                                        fontSize: '16px',
                                    }}
                                >
                                    <IconMi icon={'info-circle'} />
                                </Box>
                            )}
                        </Box>
                    )}
                    {!useInfoButton && (
                        <TileInfoIcon
                            tile={tileInfo}
                            usage={'concierge'}
                            customFieldsDisplayOnTileInfo={tileInfo.customFieldsDisplayOnTileInfo}
                        />
                    )}
                </Stack>
            </Stack>
            <ReportWhyLine why={result.why} elementId={Number(result.id)} />
            <Box sx={styles.description}>
                {tileInfo.description_markdown_ind === 'Y'
                    ? renderHtml(description)
                    : striptags(description, ['br', 'b', 'a', 'i'])}
            </Box>
        </Stack>
    );
}
